

<template>
  <section class="register-section">
    <div class="register-container">
      <div class="register-header">
        <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="0.464" width="24" height="24" rx="4.8" fill="#1890FF" />
          <path d="M14.864 3.6H20.864V9.6H14.864V3.6Z" fill="white" />
          <path d="M10.064 9.6H14.864V14.4H10.064V9.6Z" fill="white" />
          <path d="M4.064 13.2H11.264V20.4H4.064V13.2Z" fill="white" />
        </svg>

        <a-typography-title class="register-title" :level="2">Register</a-typography-title>
        <a-typography-text class="register-text">
          Welcome! Please enter your information to register.
        </a-typography-text>
      </div>

      <div v-if="isInProcess" class="register-loading">
        <LoadingSpinner color="#1890FF" />
      </div>

      <div v-if="isWarning" class="register-warning">
        {{ message }}
      </div>

      <a-form layout="vertical" :model="form" @finish="register">
        <a-form-item label="First Name" name="first_name" :rules="[{ required: true, message: 'Please enter your first name!' }]">
          <a-input v-model:value="form.first_name" placeholder="First Name" autocomplete="off">
            <template #prefix><UserOutlined /></template>
          </a-input>
        </a-form-item>

        <a-form-item label="Last Name" name="last_name" :rules="[{ required: true, message: 'Please enter your last name!' }]">
          <a-input v-model:value="form.last_name" placeholder="Last Name" autocomplete="off">
            <template #prefix><UserOutlined /></template>
          </a-input>
        </a-form-item>

        <a-form-item label="Email" name="email" :rules="[{ required: true, type: 'email', message: 'Please enter your email!' }]">
          <a-input v-model:value="form.email" placeholder="Email" autocomplete="off">
            <template #prefix><MailOutlined /></template>
          </a-input>
        </a-form-item>

        <a-form-item label="Password" name="password" :rules="[{ required: true, message: 'Please enter your password!' }]">
          <a-input-password v-model:value="form.password" placeholder="Password" autocomplete="off">
            <template #prefix><LockOutlined /></template> 
          </a-input-password>
        </a-form-item>

        <a-form-item
          label="Confirm Password"
          name="confirmPassword"
          :rules="[
            { required: true, message: 'Please confirm your password!' },
            { validator: validateConfirmPassword }
          ]"
        >
          <a-input-password v-model:value="form.confirmPassword" placeholder="Confirm Password" autocomplete="new-password">
            <template #prefix><LockOutlined /></template>
          </a-input-password>  
        </a-form-item>

        <a-form-item>
          <a-button type="primary" html-type="submit" block>Register</a-button>
          <div class="register-footer">
            <a-typography-text class="register-text">Already have an account?</a-typography-text>
            <a-typography-link href="/login"> Login </a-typography-link>
          </div>
        </a-form-item>
      </a-form>
    </div>
  </section>
</template>


<script lang="ts" setup>
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { MailOutlined, LockOutlined, UserOutlined  } from '@ant-design/icons-vue';
import LoadingSpinner from '@/components/LoadingSpinner.vue';
import { Auth } from '@/lib/auth';

const form = ref({
  email: '',
  password: '',
  first_name: '',
  last_name: '',
  confirmPassword: '',
});

const isInProcess = ref(false);
const isWarning = ref(false);
const message = ref('');
const router = useRouter();
 
const validateConfirmPassword = async (_: any, value: string) => {
  if (value !== form.value.password) {
    return Promise.reject('Passwords do not match!');
  }
  return Promise.resolve();
};

const register = async () => {
  isInProcess.value = true;
  isWarning.value = false;
  message.value = '';

  try {
    const result = await Auth.register({
      email: form.value.email,
      password: form.value.password,
      confirmPassword: form.value.confirmPassword, 
      first_name: form.value.first_name,
      last_name: form.value.last_name,
    });

    if (result.success) {
      router.push('/login');
    } else {
      isWarning.value = true;
      message.value = result.error || 'Registration failed.';
    }
  } catch (err : any) {
    isWarning.value = true;
    message.value = err.response?.data?.message || err.response?.data?.error || 'Email and password are required.';
  } finally {
    isInProcess.value = false;
  }
};

</script>

<style lang="scss" scoped>
@use './styles/register.scss' as *;
</style>